'use client';

import { useTranslations } from 'next-intl';
import Link from 'next/link';
import {
  HelpCircle,
  RefreshCw,
  Upload,
  Shield,
  CreditCard,
  MessageSquare,
  Clock,
  Mail,
  AlertCircle,
  CheckCircle,
  FileQuestion,
  Wrench
} from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Card } from '@/components/ui/Card';
import { type Locale } from '@/lib/i18n/config';

interface SupportPageClientProps {
  locale: Locale;
}

export default function SupportPageClient({ locale }: SupportPageClientProps) {
  const t = useTranslations();

  const supportCategories = [
    {
      icon: Wrench,
      title: t('support.categories.toolNotWorking.title'),
      description: t('support.categories.toolNotWorking.description'),
    },
    {
      icon: Upload,
      title: t('support.categories.fileIssues.title'),
      description: t('support.categories.fileIssues.description'),
    },
    {
      icon: Shield,
      title: t('support.categories.privacy.title'),
      description: t('support.categories.privacy.description'),
    },
    {
      icon: CreditCard,
      title: t('support.categories.credits.title'),
      description: t('support.categories.credits.description'),
    },
  ];

  const faqs = [
    {
      question: t('support.faqs.needAccount.question'),
      answer: t('support.faqs.needAccount.answer'),
    },
    {
      question: t('support.faqs.filesSafe.question'),
      answer: t('support.faqs.filesSafe.answer'),
    },
    {
      question: t('support.faqs.multipleDownloads.question'),
      answer: t('support.faqs.multipleDownloads.answer'),
    },
    {
      question: t('support.faqs.refreshPage.question'),
      answer: t('support.faqs.refreshPage.answer'),
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header locale={locale} />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[hsl(var(--color-primary)/0.1)] via-[hsl(var(--color-background))] to-[hsl(var(--color-secondary)/0.1)] py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-6">
                <HelpCircle className="h-8 w-8 text-blue-600" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-[hsl(var(--color-foreground))] mb-6">
                {t('support.hero.title')}
              </h1>
              <p className="text-lg text-[hsl(var(--color-muted-foreground))]">
                {t('support.hero.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Support Categories */}
        <section className="py-12 bg-[hsl(var(--color-muted)/0.3)]">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-[hsl(var(--color-foreground))] text-center mb-10">
              {t('support.howCanWeHelp.title')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {supportCategories.map((item, index) => {
                const Icon = item.icon;
                return (
                  <Card key={index} className="p-6 text-center" hover>
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 mb-4">
                      <Icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <h3 className="font-semibold text-[hsl(var(--color-foreground))] mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-[hsl(var(--color-muted-foreground))]">
                      {item.description}
                    </p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Support Content */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto space-y-12">

              {/* Tool Not Working */}
              <Card className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                    <Wrench className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-[hsl(var(--color-foreground))] mb-2">
                      {t('support.sections.toolNotWorking.title')}
                    </h2>
                    <p className="text-[hsl(var(--color-muted-foreground))]">
                      {t('support.sections.toolNotWorking.description')}
                    </p>
                  </div>
                </div>
                <ul className="space-y-3">
                  {[1, 2, 3, 4, 5].map((num) => (
                    <li key={num} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-[hsl(var(--color-muted-foreground))]">
                        {t(`support.sections.toolNotWorking.steps.step${num}`)}
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>

              {/* File Upload/Download Issues */}
              <Card className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Upload className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-[hsl(var(--color-foreground))] mb-2">
                      {t('support.sections.fileIssues.title')}
                    </h2>
                    <p className="text-[hsl(var(--color-muted-foreground))]">
                      {t('support.sections.fileIssues.description')}
                    </p>
                  </div>
                </div>
                <ul className="space-y-3">
                  {[1, 2, 3, 4].map((num) => (
                    <li key={num} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-[hsl(var(--color-muted-foreground))]">
                        {t(`support.sections.fileIssues.steps.step${num}`)}
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>

              {/* Privacy and Security */}
              <Card className="p-8 bg-gradient-to-br from-green-50 to-transparent border-green-200">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                    <Shield className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-[hsl(var(--color-foreground))] mb-2">
                      {t('support.sections.privacy.title')}
                    </h2>
                    <p className="text-[hsl(var(--color-muted-foreground))] mb-4">
                      {t('support.sections.privacy.description')}
                    </p>
                  </div>
                </div>
                <ul className="space-y-3">
                  {[1, 2, 3].map((num) => (
                    <li key={num} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-[hsl(var(--color-muted-foreground))]">
                        {t(`support.sections.privacy.points.point${num}`)}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-[hsl(var(--color-muted-foreground))] mt-4">
                  {t('support.sections.privacy.footer')}{' '}
                  <Link href="/privacy" className="text-[hsl(var(--color-primary))] hover:underline font-medium">
                    {t('support.sections.privacy.privacyPolicyLink')}
                  </Link>
                </p>
              </Card>

              {/* Credits, Plans, and Usage */}
              <Card className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                    <CreditCard className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-[hsl(var(--color-foreground))] mb-2">
                      {t('support.sections.credits.title')}
                    </h2>
                    <p className="text-[hsl(var(--color-muted-foreground))]">
                      {t('support.sections.credits.description')}
                    </p>
                  </div>
                </div>
              </Card>

            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-12 bg-[hsl(var(--color-muted)/0.3)]">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <FileQuestion className="h-12 w-12 text-[hsl(var(--color-primary))] mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-[hsl(var(--color-foreground))] mb-3">
                  {t('support.faq.title')}
                </h2>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <Card key={index} className="p-6" hover>
                    <h3 className="font-semibold text-[hsl(var(--color-foreground))] mb-3 flex items-start gap-2">
                      <HelpCircle className="h-5 w-5 text-[hsl(var(--color-primary))] flex-shrink-0 mt-0.5" />
                      {faq.question}
                    </h3>
                    <p className="text-[hsl(var(--color-muted-foreground))] pl-7">
                      {faq.answer}
                    </p>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact Support */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <Card className="p-8 bg-gradient-to-br from-[hsl(var(--color-primary)/0.05)] to-transparent">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[hsl(var(--color-primary)/0.1)] mb-4">
                    <MessageSquare className="h-8 w-8 text-[hsl(var(--color-primary))]" />
                  </div>
                  <h2 className="text-3xl font-bold text-[hsl(var(--color-foreground))] mb-3">
                    {t('support.contact.title')}
                  </h2>
                  <p className="text-[hsl(var(--color-muted-foreground))] mb-6">
                    {t('support.contact.description')}
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="flex items-center gap-3 p-4 bg-[hsl(var(--color-card))] rounded-lg border border-[hsl(var(--color-border))]">
                    <Mail className="h-6 w-6 text-[hsl(var(--color-primary))]" />
                    <div>
                      <p className="text-sm text-[hsl(var(--color-muted-foreground))] mb-1">
                        {t('support.contact.email.label')}
                      </p>
                      <a
                        href="mailto:support@pdfyork.com"
                        className="font-medium text-[hsl(var(--color-primary))] hover:underline"
                      >
                        support@pdfyork.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-4 bg-[hsl(var(--color-card))] rounded-lg border border-[hsl(var(--color-border))]">
                    <Clock className="h-6 w-6 text-[hsl(var(--color-primary))]" />
                    <div>
                      <p className="text-sm text-[hsl(var(--color-muted-foreground))] mb-1">
                        {t('support.contact.hours.label')}
                      </p>
                      <p className="font-medium text-[hsl(var(--color-foreground))]">
                        {t('support.contact.hours.value')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h3 className="font-semibold text-[hsl(var(--color-foreground))] mb-3 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-blue-600" />
                    {t('support.contact.whenContacting.title')}
                  </h3>
                  <ul className="space-y-2">
                    {[1, 2, 3, 4].map((num) => (
                      <li key={num} className="flex items-start gap-2 text-sm text-[hsl(var(--color-muted-foreground))]">
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                        {t(`support.contact.whenContacting.items.item${num}`)}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="text-center mt-6">
                  <p className="text-sm text-[hsl(var(--color-muted-foreground))] mb-1">
                    {t('support.contact.responseTime.label')}
                  </p>
                  <p className="font-medium text-[hsl(var(--color-foreground))]">
                    {t('support.contact.responseTime.value')}
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* Updates & Maintenance */}
        <section className="py-12 bg-[hsl(var(--color-muted)/0.3)]">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <Card className="p-8 text-center">
                <RefreshCw className="h-12 w-12 text-[hsl(var(--color-primary))] mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-[hsl(var(--color-foreground))] mb-3">
                  {t('support.updates.title')}
                </h2>
                <p className="text-[hsl(var(--color-muted-foreground))]">
                  {t('support.updates.description')}
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Need More Help */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl font-bold text-[hsl(var(--color-foreground))] mb-4">
                {t('support.needMoreHelp.title')}
              </h2>
              <p className="text-[hsl(var(--color-muted-foreground))] mb-6">
                {t('support.needMoreHelp.description')}
              </p>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[hsl(var(--color-primary))] text-white rounded-lg hover:bg-[hsl(var(--color-primary)/0.9)] transition-colors font-medium"
              >
                <MessageSquare className="h-5 w-5" />
                {t('support.needMoreHelp.contactButton')}
              </Link>
            </div>
          </div>
        </section>

      </main>

      <Footer locale={locale} />
    </div>
  );
}
